/**
 * RecentCreativesCarousel — sanitized "live moodboard" of the latest 12
 * creatives generated by real users, shown on the landing page beneath the
 * StatsTicker as visual social proof.
 *
 * Why this works: counters are abstract ("12,481 creatives generated") —
 * actually SEEING a row of real outputs makes the value prop concrete in
 * one glance. The backend (/api/public/recent-creatives) strips all PII
 * (user_id, product_id, captions, prompts) so this is safe to render to
 * anonymous visitors.
 *
 * Behaviour:
 *  - Polls once on mount + every 90 s
 *  - Auto-scrolls in an infinite marquee on hover-pause
 *  - Videos use <video muted autoplay loop> with a still-frame poster
 *  - Items lazy-load; failed images are silently dropped (better gap than
 *    a broken-image icon during a moodboard render)
 *  - Hides itself entirely if API errors or returns 0 items
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Sparkles, Play } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_INTERVAL_MS = 90_000;

// ─── Single tile (image or muted-loop video) ───────────────────────────
const MediaTile = ({ item, idx, onFailed }) => {
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  // Match aspect classes — keeps the marquee height uniform
  const aspectCls = {
    "9:16": "aspect-[9/16]",
    "1:1":  "aspect-square",
    "16:9": "aspect-video",
    "4:5":  "aspect-[4/5]",
  }[item.aspect_ratio] || "aspect-[4/5]";

  const handleFailed = () => {
    if (!failed) {
      setFailed(true);
      onFailed?.(item.media_url);
    }
  };

  if (failed) return null;

  return (
    <div
      className={`relative ${aspectCls} w-[140px] sm:w-[180px] flex-shrink-0 rounded-2xl overflow-hidden bg-ink-100 border border-ink-200/70 shadow-sm`}
      data-testid={`recent-tile-${idx}`}
      data-kind={item.kind}
    >
      {/* Skeleton until media reports loaded */}
      {!loaded && (
        <div className="absolute inset-0 bg-gradient-to-br from-ink-100 via-ink-50 to-ink-100 animate-pulse" />
      )}
      {item.kind === "video" ? (
        <video
          src={item.media_url}
          muted
          autoPlay
          loop
          playsInline
          preload="metadata"
          onLoadedData={() => setLoaded(true)}
          onError={handleFailed}
          className="w-full h-full object-cover"
        />
      ) : (
        <img
          src={item.media_url}
          alt=""
          loading="lazy"
          onLoad={() => setLoaded(true)}
          onError={handleFailed}
          className="w-full h-full object-cover"
        />
      )}
      {/* tiny kind badge in the corner */}
      <span className="absolute top-2 left-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-ink-950/70 backdrop-blur text-white text-[9px] font-bold uppercase tracking-[0.16em]">
        {item.kind === "video" ? <Play className="w-2.5 h-2.5" /> : <Sparkles className="w-2.5 h-2.5" />}
        {item.kind}
      </span>
    </div>
  );
};

export const RecentCreativesCarousel = () => {
  const [items, setItems] = useState([]);
  const [errored, setErrored] = useState(false);
  const [failedUrls, setFailedUrls] = useState(() => new Set());
  const stopRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    const fetchItems = async () => {
      try {
        const r = await axios.get(`${API}/public/recent-creatives`);
        if (!cancelled) {
          setItems(r.data?.items || []);
          setFailedUrls(new Set()); // reset on refresh
          setErrored(false);
        }
      } catch (_) {
        if (!cancelled) setErrored(true);
      }
    };
    fetchItems();
    const id = setInterval(fetchItems, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  const onTileFailed = (url) => {
    setFailedUrls((prev) => {
      if (prev.has(url)) return prev;
      const next = new Set(prev);
      next.add(url);
      return next;
    });
  };

  // Filter out URLs that already errored (e.g. expired Kling CDN urls).
  // The band hides itself entirely if 0 valid items remain — cleaner than
  // showing a header with an empty marquee underneath.
  const visibleItems = items.filter((it) => !failedUrls.has(it.media_url));
  if (errored || visibleItems.length === 0) return null;

  // Duplicate the list so the marquee scrolls infinitely without seams
  const marquee = [...visibleItems, ...visibleItems];

  return (
    <section
      className="relative bg-white border-b border-ink-200 py-10 overflow-hidden"
      data-testid="recent-creatives-carousel"
    >
      <div className="px-6 max-w-6xl mx-auto mb-6 flex items-center justify-between gap-4">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-coral-500">
            Live moodboard
          </p>
          <h2 className="font-display font-bold text-xl text-ink-950 mt-1">
            Made on XEA · last hour
          </h2>
        </div>
        <span
          className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-ink-500"
          data-testid="recent-creatives-count"
        >
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
          {visibleItems.length} fresh
        </span>
      </div>

      {/* Marquee row */}
      <div
        className="relative"
        onMouseEnter={() => (stopRef.current = true)}
        onMouseLeave={() => (stopRef.current = false)}
      >
        {/* Edge fade masks so tiles fade in/out at viewport bounds */}
        <div className="pointer-events-none absolute inset-y-0 left-0 w-16 bg-gradient-to-r from-white to-transparent z-10" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-16 bg-gradient-to-l from-white to-transparent z-10" />
        <div className="moodboard-marquee flex gap-4">
          {marquee.map((it, i) => (
            <MediaTile
              key={`${it.media_url}-${i}`}
              item={it}
              idx={i}
              onFailed={onTileFailed}
            />
          ))}
        </div>
      </div>

      <style>{`
        .moodboard-marquee {
          animation: moodboardScroll 60s linear infinite;
          width: max-content;
          padding-inline: 1.5rem;
        }
        .moodboard-marquee:hover { animation-play-state: paused; }
        @keyframes moodboardScroll {
          from { transform: translateX(0); }
          to   { transform: translateX(-50%); }
        }
        @media (prefers-reduced-motion: reduce) {
          .moodboard-marquee { animation: none; }
        }
      `}</style>
    </section>
  );
};

export default RecentCreativesCarousel;
